import React, { FC, useEffect, useState } from 'react'
import Form, { FormComponentProps } from 'antd/lib/form'
import { Heading, Indent, UITypes, PostalCodeInput, i18n } from 'LibNeo'
import { FormNames } from 'Src/constants/FormNames'
import { FieldNames } from 'Src/constants/FieldNames'
import {
	ADDRESS_TYPE,
	DATA_FIELDS_WITH_TYPE,
	FIELD_PLACEHOLDERS,
	FIELDS,
	mappingShortLongList,
} from 'Src/constants/Address.constants'
import { getStringSource } from 'Helpers'
import { isNil } from 'lodash'
import { BaseAddress } from 'Models/address/BaseAddress'
import { AddressSuggest } from 'Models'
import { Callback } from 'Src/types/utilTypes'
import { AddressEditSelect } from './AddressEditSelect'
import { AddressEditWrapper, FinalPlaceAlert, FormBlock, SaveAddress } from './AdressEditStyled'

interface AddressEditProps extends FormComponentProps {
	initialData: AddressSuggest
	saveEditAddress: (address: BaseAddress) => void
	close?: Callback
	disabled?: boolean
	typeAddress?: ADDRESS_TYPE
}

const AddressEdit: FC<AddressEditProps> = (props) => {
	const { initialData, saveEditAddress, close, form, disabled, typeAddress } = props
	const DATA_FIELDS = DATA_FIELDS_WITH_TYPE(typeAddress)
	const { getFieldsError, getFieldsValue, getFieldValue, validateFields } = form
	const [disableBtn, setDisableBtn] = useState(false)
	const formErrors = Object.values(getFieldsError()).filter((error) => !isNil(error))

	useEffect(() => {
		setDisableBtn(Boolean(formErrors.length))
	}, [formErrors.length])

	const postCode = getFieldValue(FieldNames.POSTAL_CODE)

	useEffect(() => {
		if (!postCode) {
			validateFields([FieldNames.POSTAL_CODE])
		}
	}, [postCode, validateFields])

	const getPrepareAddress = (values: Record<FieldNames, string | undefined>): BaseAddress => {
		const newAddress = {} as BaseAddress
		newAddress[FieldNames.POSTAL_CODE] = values?.[FieldNames.POSTAL_CODE]
		FIELDS.forEach((field) => {
			const dataField = DATA_FIELDS[field]
			const fieldType = dataField.field_type
			const fieldTypeShort =
				'field_type_short' in dataField ? dataField.field_type_short : undefined
			newAddress[field] = values?.[field]
			newAddress[fieldType] = values?.[fieldType]
			if (fieldTypeShort) {
				newAddress[fieldTypeShort] =
					mappingShortLongList?.[values?.[fieldType]] || values?.[fieldType]
			}
		})

		newAddress.result = getStringSource(newAddress, typeAddress)

		return newAddress
	}

	const submitForm = () => {
		const { validateFields, resetFields } = form
		validateFields((err, values: Record<FieldNames, string | undefined>) => {
			if (!err) {
				const newAddress = getPrepareAddress(values)
				saveEditAddress(newAddress)
				resetFields()
				close?.()
			}
		})
	}

	const values = getFieldsValue() as Record<FieldNames, string | undefined>
	const finalAddress = getPrepareAddress(values)?.result || initialData?.result
	const selectPlaceholder = i18n('company.chose-select')

	return (
		<AddressEditWrapper>
			<Indent bottom={20}>
				<Heading type="h5">Уточнение адреса</Heading>
			</Indent>
			<FormBlock bottom={12}>
				<div>
					<PostalCodeInput
						form={form}
						placeholder=""
						required
						initialValue={initialData?.postal_code || ''}
					/>
				</div>
			</FormBlock>
			{FIELDS.map((field) => {
				const dataField = DATA_FIELDS[field]
				return (
					<AddressEditSelect
						key={field}
						field={field}
						form={form}
						initialData={initialData}
						label={dataField.name_type}
						allowClear
						inputPlaceHolder={FIELD_PLACEHOLDERS[field] ?? i18n('common.empty_name')}
						placeholder={selectPlaceholder}
						typeAddress={typeAddress}
					/>
				)
			})}
			{finalAddress && (
				<FormBlock withSpacing bottom={12} top={20}>
					<FinalPlaceAlert type={UITypes.INFO} title={finalAddress} icon="location-pos" />
				</FormBlock>
			)}
			<Indent top={20}>
				<SaveAddress
					block
					onClick={submitForm}
					disabled={disabled || disableBtn}
					title="address.save_address"
				/>
			</Indent>
		</AddressEditWrapper>
	)
}

export const AddressEditComponent = Form.create<AddressEditProps>({
	name: FormNames.EDIT_ADDRESS_RBIDOS_REQUEST_FORM,
})(AddressEdit)
